import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GraphQLConfig } from '@/types/mcpTool';
import { Code, Sparkles, FileJson, Lightbulb, Copy, CheckCircle } from 'lucide-react';

interface GraphQLQueryBuilderProps {
  value: GraphQLConfig;
  onChange: (config: GraphQLConfig) => void;
  onContextTokenInsert?: () => void;
}

/**
 * GraphQLQueryBuilder Component
 *
 * Builder for GraphQL queries and mutations
 * Features:
 * - Query/Mutation editor with syntax hints
 * - Variables editor (JSON)
 * - Operation name field
 * - Example query templates
 * - Validation
 */
export const GraphQLQueryBuilder: React.FC<GraphQLQueryBuilderProps> = ({
  value,
  onChange,
  onContextTokenInsert,
}) => {
  const [queryError, setQueryError] = useState<string | null>(null);
  const [variablesError, setVariablesError] = useState<string | null>(null);
  const [copiedTemplate, setCopiedTemplate] = useState<string | null>(null);

  const handleQueryChange = (query: string) => {
    setQueryError(null);
    onChange({ ...value, query });
  };

  const handleVariablesChange = (variablesJson: string) => {
    try {
      const parsed = variablesJson ? JSON.parse(variablesJson) : undefined;
      setVariablesError(null);
      onChange({ ...value, variables: parsed });
    } catch (e) {
      setVariablesError('Invalid JSON format');
    }
  };

  const handleOperationNameChange = (operationName: string) => {
    onChange({ ...value, operationName: operationName || undefined });
  };

  const applyTemplate = (template: { query: string; variables?: Record<string, any>; description: string }) => {
    onChange({
      query: template.query,
      variables: template.variables,
      operationName: undefined,
    });
    setCopiedTemplate(template.description);
    setTimeout(() => setCopiedTemplate(null), 2000);
  };

  const exampleTemplates = [
    {
      description: 'Simple Query',
      query: `query GetUser($id: ID!) {
  user(id: $id) {
    id
    name
    email
  }
}`,
      variables: { id: "user_123" },
    },
    {
      description: 'Mutation',
      query: `mutation CreateUser($input: CreateUserInput!) {
  createUser(input: $input) {
    id
    name
    email
    createdAt
  }
}`,
      variables: {
        input: {
          name: "John Doe",
          email: "john@example.com"
        }
      },
    },
    {
      description: 'List Query',
      query: `query ListUsers($limit: Int, $offset: Int) {
  users(limit: $limit, offset: $offset) {
    totalCount
    items {
      id
      name
      email
    }
  }
}`,
      variables: { limit: 10, offset: 0 },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code className="h-4 w-4 text-purple-600" />
          <h4 className="text-sm font-medium">GraphQL Configuration</h4>
          <Badge className="bg-purple-100 text-purple-700">
            GraphQL
          </Badge>
        </div>
      </div>

      {/* Example Templates */}
      <Card className="p-3 bg-purple-50 border-purple-200">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="h-4 w-4 text-purple-600" />
          <p className="text-xs font-medium text-purple-900">Quick Start Templates</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {exampleTemplates.map((template) => (
            <Button
              key={template.description}
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs bg-white hover:bg-purple-100"
              onClick={() => applyTemplate(template)}
            >
              {copiedTemplate === template.description ? (
                <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
              ) : (
                <Copy className="h-3 w-3 mr-1" />
              )}
              {template.description}
            </Button>
          ))}
        </div>
      </Card>

      {/* Query Editor */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="graphql-query">
            GraphQL Query/Mutation <span className="text-red-500">*</span>
          </Label>
          {onContextTokenInsert && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={onContextTokenInsert}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Insert Token
            </Button>
          )}
        </div>
        <Textarea
          id="graphql-query"
          placeholder={`query GetCustomer($id: ID!) {
  customer(id: $id) {
    id
    name
    email
  }
}`}
          value={value.query}
          onChange={(e) => handleQueryChange(e.target.value)}
          rows={12}
          className="font-mono text-sm max-w-full resize-none overflow-x-auto"
        />
        {queryError && (
          <p className="text-xs text-red-600">{queryError}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Code className="h-3 w-3" />
          <span>Write your GraphQL query or mutation</span>
        </div>
      </div>

      {/* Operation Name (Optional) */}
      <div className="space-y-2">
        <Label htmlFor="operation-name">Operation Name (Optional)</Label>
        <Input
          id="operation-name"
          placeholder="GetCustomer"
          value={value.operationName || ''}
          onChange={(e) => handleOperationNameChange(e.target.value)}
        />
        <p className="text-xs text-gray-500">
          Useful when your query contains multiple operations
        </p>
      </div>

      {/* Variables Editor */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <FileJson className="h-4 w-4 text-gray-400" />
          <Label htmlFor="graphql-variables">Variables (JSON)</Label>
        </div>
        <Textarea
          id="graphql-variables"
          placeholder={`{
  "id": "cus_123",
  "limit": 10
}`}
          value={value.variables ? JSON.stringify(value.variables, null, 2) : ''}
          onChange={(e) => handleVariablesChange(e.target.value)}
          rows={6}
          className="font-mono text-sm max-w-full resize-none overflow-x-auto"
        />
        {variablesError && (
          <p className="text-xs text-red-600">{variablesError}</p>
        )}
        <p className="text-xs text-gray-500">
          JSON object containing values for query variables
        </p>
      </div>

      {/* GraphQL Tips */}
      <Alert className="border-blue-200 bg-blue-50">
        <Lightbulb className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-900 text-xs">
          <strong>Tips:</strong> Use <code className="bg-blue-100 px-1 rounded">$variable</code> syntax for
          dynamic values. Variables can use context tokens like <code className="bg-blue-100 px-1 rounded">{"{{user.id}}"}</code>.
        </AlertDescription>
      </Alert>

      {/* Preview */}
      {value.query && (
        <Card className="p-3 bg-gray-50 overflow-x-auto max-w-full">
          <p className="text-xs font-medium text-gray-600 mb-2">Request Preview:</p>
          <pre className="text-xs text-gray-800 overflow-x-auto max-w-full whitespace-pre-wrap break-words">
{JSON.stringify(
  {
    query: value.query,
    variables: value.variables,
    operationName: value.operationName,
  },
  null,
  2
)}
          </pre>
        </Card>
      )}
    </div>
  );
};
